<script setup>
import axios from "axios";
import { ref, onBeforeMount } from "vue";

const data = ref({});
const loading = ref(true);
const activeYear = ref(null);
const sortBy = ref("street"); // Default sorting by street name
const sortDirection = ref("asc"); // Default sorting direction

// Months array
const months = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const fetchData = async () => {
  try {
    const response = await axios.get("request_count_by_street/");
    data.value = response.data;

    // Set the activeYear to the first year in the data after fetching
    if (Object.keys(data.value).length > 0) {
      activeYear.value = Object.keys(data.value)[0];
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    loading.value = false; // Set loading to false after fetching
  }
};

const setActiveYear = (year) => {
  activeYear.value = year;
};

// Function to get unique street names for a given year
const getUniqueStreets = (yearData) => {
  const streets = new Set();
  for (const monthData of Object.values(yearData)) {
    for (const street of monthData) {
      streets.add(street.street_name);
    }
  }
  return Array.from(streets);
};

// Function to get the request count for a specific street and month
const getRequestCount = (yearData, streetName, monthName) => {
  const monthIndex = months.indexOf(monthName) + 1; // Convert month name to index
  if (yearData[monthIndex]) {
    const streetData = yearData[monthIndex].find(
        (street) => street.street_name === streetName
    );
    return streetData ? streetData.request_count : 0;
  }
  return 0;
};

// Function to sort streets
const sortStreets = (streets, yearData) => {
  return streets.sort((a, b) => {
    if (sortBy.value === "street") {
      return sortDirection.value === "asc" ? a.localeCompare(b) : b.localeCompare(a);
    } else {
      // const monthIndex = months.indexOf(sortBy.value) + 1;
      const countA = getRequestCount(yearData, a, sortBy.value);
      const countB = getRequestCount(yearData, b, sortBy.value);
      return sortDirection.value === "asc" ? countA - countB : countB - countA;
    }
  });
};

// Function to handle sorting
const handleSort = (column) => {
  if (sortBy.value === column) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortBy.value = column;
    sortDirection.value = "asc";
  }
};

// Fetch data when the component is mounted
onBeforeMount(() => {
  fetchData();
});
</script>

<template>
  <!-- Show loading state while data is being fetched -->
  <div v-if="loading">Загрузка...</div>

  <!-- Display data once fetched -->
  <div v-else>
    <!-- Tabs for Years -->
    <ul class="nav nav-tabs" id="yearTabs" role="tablist">
      <li class="nav-item" v-for="year in Object.keys(data)" :key="year">
        <h4
            class="nav-link"
            :class="{ active: activeYear === year }"
            @click="setActiveYear(year)"
        >
          {{ year }}
        </h4>
      </li>
    </ul>

    <!-- Tab Content -->
    <div class="tab-content">
      <div v-if="activeYear">
        <!-- Responsive Table Wrapper -->
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm">
            <thead>
            <tr>
              <th @click="handleSort('street')">
                Улицы
                <span v-if="sortBy === 'street'">
                    <i
                        :class="
                        sortDirection === 'asc'
                          ? ['bi bi-sort-alpha-down']
                          : ['bi bi-sort-alpha-down-alt']
                      "
                    ></i>
                  </span>
              </th>
              <th
                  v-for="month in months"
                  :key="month"
                  @click="handleSort(month)"
              >
                {{ month }}
                <span v-if="sortBy === month">
                    <i
                        :class="
                        sortDirection === 'asc'
                          ? ['bi bi-sort-down-alt']
                          : ['bi bi-sort-down']
                      "
                    ></i>
                  </span>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="street in sortStreets(
                  getUniqueStreets(data[activeYear]),
                  data[activeYear]
                )"
                :key="street"
            >
              <td>{{ street }}</td>
              <td v-for="month in months" :key="month">
                {{ getRequestCount(data[activeYear], street, month) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Compact table styling */
.table-sm th,
.table-sm td {
  padding: 0.25rem; /* Reduce padding */
  font-size: 0.875rem; /* Smaller font size */
}

.nav-tabs .nav-link {
  cursor: pointer;
  font-size: 0.875rem; /* Smaller font size for tabs */
  padding: 0.5rem 1rem; /* Reduce padding for tabs */
}

th {
  cursor: pointer;
  white-space: nowrap; /* Prevent text wrapping */
}

.table-responsive {
  overflow-x: auto;
}

/* Optional: Add a hover effect to table rows */
.table-striped tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>